/**
 * @file
 * Container for web/native communication and options.
 *
 * @example
 * $notify($app.option.platform);
 * @example
 * $notify($app.option.theme);
 */


import {
    setTimeZone
} from './date';
import {
    setLanguage
} from './locale';
import {
    setFontSize,
    setTheme,
    setTraditionalChineseColors
} from './media';
import {
    setPrivacyMode
} from '../directive/privacy';


const fontBase =
    $build.platform == 'desktop' ? 14 :
    $build.platform == 'impact' ? 15 :
    16;

const embedded =
    $build.platform == 'android' ||
    $build.platform == 'desktop' ||
    $build.platform == 'impact' ||
    $build.platform == 'ios';

let listeners = [];
/** @ignore */
let $app = window.$app || {};


if ($app.send === void 0) {
    if ($app.sendJSON) {
        // Android may provide sendJSON but not send
        /** Send a message to the native side. */
        $app.send = data => $app.sendJSON(JSON.stringify(data));
    } else {
        // Use sendToNativeApp where and when available
        let started = Date.now();
        /** @ignore */
        $app.send = data => {
            if (window.sendToNativeApp) {
                /** @ignore */
                $app.send = window.sendToNativeApp;
                $app.send(data);
            } else if (Date.now() < started + 2000) {
                setTimeout(() => { $app.send(data); }, 20);
            } else {
                console.error('$app.send: not ready');
            }
        }
    }
}

if ($app.receive === void 0) {
    /** Receive a message from the native side and forward it to all listeners. */
    $app.receive = data =>
        setTimeout(() => { listeners.forEach(listener => listener.receive(data)); });
}

if ($app.addListener === void 0) {
    /**
     * Register a listener for messages from the native side.
     * @param {Object} listener - An object that defines a `receive` method.
     */
    $app.addListener = listener =>
        listeners.indexOf(listener) < 0 && listeners.push(listener);
}

if ($app.removeListener === void 0) {
    /**
     * Unregister a listener for messages from the native side.
     * @param {Object} listener - The object to unregister.
     */
    $app.removeListener = listener =>
        listeners = listeners.filter(e => e !== listener);
}


/**
 * @function requestedOption
 * @memberof $app
 * @description Optional function that may be defined by the platform, which returns a collection of requested options as a JSON string. This is an alternative to defining `$app.option` directly from a platform where declaring it directly is impossible or would impede reactivity.
 */
const option = $app.requestedOption
    ? JSON.parse($app.requestedOption())
    : { ...$app.option };


/**
 * @member option
 * @memberof $app
 * @description Lib.Common configuration. For embedded apps, this is defined by the platform.
 */

/**
 * @member englishNumbers
 * @memberof $app.option
 * @description If `true`, do not localize number formatting.
 */
if (option.englishNumbers === void 0) {
    option.englishNumbers = embedded;
}

/**
 * @member fontSize
 * @memberof $app.option
 * @description Root font size in pixels. Retrieve via [`$media.fontSize`](#/global/media).
 */
if (option.fontSize === void 0) {
    option.fontSize = fontBase;
}

/**
 * @member language
 * @memberof $app.option
 * @description Requested locale (language/script/region) code. Retrieve via [`$locale`](#/global/locale).
 */
if (option.language === void 0) {
    option.language = embedded
        ? location.hash.match(/[?&]lang=([\w-]+)(?=&|$)|$/)[1]
        : window.AM_LOGIN_LANGUAGE || document.cookie.match(/(?:^|; )IB_LANG=([\w-]+)(?=;|$)|$/)[1];
}

/**
 * @member platform
 * @memberof $app.option
 * @description The name of the platform on which the app is running. Libraries may use this as a runtime alternative to `$build.platform`.
 */
if (option.platform === void 0) {
    option.platform = $build.platform;
}

/**
 * @member privacyMode
 * @memberof $app.option
 * @description If `true`, the [`v-privacy`](#/directive/v-privacy) directive and the `privacy` prop on [`ib-field`](#/components/form/ib-field) will mask sensitive content.
 */
if (option.privacyMode === void 0) {
    option.privacyMode = embedded
        ? new URLSearchParams(location.search).get('privacyMode') == 'true'
        : false;
} else if (typeof option.privacyMode === 'string') {
    option.privacyMode = option.privacyMode == 'true';
}

/**
 * @member theme
 * @memberof $app.option
 * @description Color scheme (`auto`, `dark`, or `light`). `auto` will use (and react to changes in) the device color scheme. Retrieve via [`$media.theme`](#/global/media).
 */
if (option.theme === void 0) {
    option.theme = embedded && !'%IB_THEME%'.startsWith('%')
        ? '%IB_THEME%'
        : getComputedStyle(document.documentElement).getPropertyValue('--theme') || (document.querySelector('link[data-theme]')?.href.includes('.dark.') ? 'dark' : 'light');
}

/**
 * @member timeZone
 * @memberof $app.option
 * @description IANA time zone name; this can be configured in Desktop TWS independently of device time zone.
 */
if (option.timeZone === void 0) {
    option.timeZone = new URLSearchParams(location.hash.replace(/[^?]*/,'')).get('tz') || void 0;
}

/**
 * @member traditionalChineseColors
 * @memberof $app.option
 * @description If `true`, invert rise and fall (green and red) colors.
 */
if (option.traditionalChineseColors === void 0) {
    option.traditionalChineseColors = false;
}


/** Setters */
const store = {...option};


/** Make options reactive */
Object.defineProperty($app, 'option', {
    get: () => option,
    set: value => {
        Object.assign(option, value);
    }
});

Object.defineProperty(option, 'fontSize', {
    get: () => store.fontSize,
    set: fontSize => {
        setFontSize(store.fontSize = fontSize);
    }
});

Object.defineProperty(option, 'language', {
    get: () => store.language,
    set: language => {
        setLanguage(store.language = language, store.englishNumbers);
    }
});

Object.defineProperty(option, 'privacyMode', {
    get: () => store.privacyMode,
    set: privacyMode => {
        if ($build.platform == 'desktop') {
            //TWSRETAIL-11336: dTWS sends privacyMode as a string instead of boolean
            setPrivacyMode(store.privacyMode = privacyMode === true || privacyMode === 'true');
        } else {
            setPrivacyMode(store.privacyMode = privacyMode);
        }
    }
});

Object.defineProperty(option, 'theme', {
    get: () => store.theme,
    set: theme => {
        setTheme(store.theme = theme);
    }
});

Object.defineProperty(option, 'timeZone', {
    get: () => store.timeZone,
    set: timeZone => {
        setTimeZone(store.timeZone = timeZone);
    }
});

Object.defineProperty(option, 'traditionalChineseColors', {
    get: () => store.traditionalChineseColors,
    set: traditionalChineseColors => {
        setTraditionalChineseColors(store.traditionalChineseColors = traditionalChineseColors);
    }
});

if (embedded) {
    addEventListener('hashchange', () => {
        const theme = location.hash.match(/[?&]theme=(.+?)(&|$)/);
        if (theme) {
            option.theme = theme[1];
        }
    });
}


/** Make options reactive in Vue */
Object.keys(option).forEach(key => {
    Vue.util.defineReactive(option, key, option[key]);
});


/** Initial state */
setTimeZone(option.timeZone);
setFontSize(option.fontSize);
setLanguage(option.language, option.englishNumbers);
setPrivacyMode(option.privacyMode);
setTheme(option.theme);
setTraditionalChineseColors(option.traditionalChineseColors);


/** Handle messages received from older clients that do not call `$app.receive` directly */
window.sendToWebApp = msg => {
    setTimeout(() => {
        $app.receive(msg);
    });
};


/**
 * @function requestedParam
 * @memberof $app
 * @description Optional function that may be defined by the platform, which returns a collection of requested app parms as a JSON string. This is an alternative to defining `$app.param` directly from a platform where declaring it directly is impossible or would impede reactivity.
 */
/**
 * @description Application configuration. The content is defined by the application.
 */
$app.param = $app.requestedParam
    ? JSON.parse($app.requestedParam())
    : { ...$app.param };


export default $app;
